import { Box, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Children, type CSSProperties, cloneElement, forwardRef, type HTMLAttributes, isValidElement, type ReactElement, type ReactNode } from "react";
import { List, type RowComponentProps } from "react-window";

// Loose `children` typing + `HTMLElement` ref so this is assignable to MUI's
// `AutocompleteSlots["listbox"]` signature (`JSXElementConstructor<HTMLAttributes<HTMLElement>>`).
interface VirtualizedListBoxProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
	children?: ReactNode;
}

const RowComponent = ({ itemData, index, style }: RowComponentProps<{ itemData: ReactElement[] }>) => {
	const child = itemData[index];
	if (!isValidElement(child)) return <></>;
	// Merge the virtualizer's positional style on top of the child's own style so caller-applied
	// `color` / `opacity` / `borderBottom` (set in `renderOption`) survive the cloneElement.
	const childStyle = (child.props as { style?: CSSProperties }).style ?? {};
	return cloneElement(child, {
		style: { ...childStyle, ...style },
	} as Partial<CSSProperties>);
};

// MUI's `MuiAutocomplete-listbox` className applies 8px top + 8px bottom padding.
// We size the listbox to fit `MAX_VISIBLE_ROWS` row heights plus that padding so the bounds
// match a non-virtualized listbox exactly.
const LISTBOX_VERTICAL_PADDING = 8;
const MAX_VISIBLE_ROWS = 8;

/**
 * Virtualized listbox slot for MUI's `<Autocomplete>` — drop into `slots.listbox`.
 *
 * MUI's option hover / focused / selected / padding styles live on its internal styled
 * `AutocompleteListbox` component (scoped via an emotion-generated className). When you
 * supply a custom listbox slot those styles never get registered, so the className
 * `MuiAutocomplete-listbox` alone isn't enough. We replicate the relevant rules here
 * via `sx` so options keep their MUI feel under virtualization.
 *
 * Layout:
 *   - Outer `<ul>` (Box) provides the listbox padding + max-height.
 *   - Inner `<div>` from react-window owns scrolling + absolute item positioning.
 */
export const VirtualizedListBoxComponent = forwardRef<HTMLElement, VirtualizedListBoxProps>(({ children, ...other }, ref) => {
	const itemData = Children.toArray(children).filter(isValidElement) as ReactElement[];
	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
	const itemSize = smUp ? 36 : 48;
	const visibleRows = Math.min(MAX_VISIBLE_ROWS, Math.max(1, itemData.length));
	const innerHeight = visibleRows * itemSize;
	const outerMaxHeight = innerHeight + 2 * LISTBOX_VERTICAL_PADDING;

	return (
		<Box
			component="ul"
			ref={ref as React.Ref<HTMLUListElement>}
			{...other}
			sx={{
				listStyle: "none",
				margin: 0,
				padding: `${LISTBOX_VERTICAL_PADDING}px 0`,
				maxHeight: outerMaxHeight,
				overflow: "hidden",
				position: "relative",
				isolation: "isolate",
				"& .MuiAutocomplete-option": {
					display: "flex",
					overflow: "hidden",
					justifyContent: "flex-start",
					alignItems: "center",
					cursor: "pointer",
					boxSizing: "border-box",
					outline: 0,
					padding: "6px 16px",
					WebkitTapHighlightColor: "transparent",
					"&:hover": {
						backgroundColor: theme.palette.action.hover,
						"@media (hover: none)": { backgroundColor: "transparent" },
					},
					"&.Mui-focused": {
						backgroundColor: theme.palette.action.hover,
						"@media (hover: none)": { backgroundColor: "transparent" },
					},
					"&.Mui-focusVisible": {
						backgroundColor: theme.palette.action.focus,
					},
					"&[aria-selected='true']": {
						backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
						"&.Mui-focused": {
							backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
						},
					},
					"&[aria-disabled='true']": {
						opacity: theme.palette.action.disabledOpacity,
						pointerEvents: "none",
					},
				},
			}}
		>
			<List
				tagName="div"
				style={{ height: innerHeight, maxHeight: innerHeight }}
				rowProps={{ itemData }}
				defaultHeight={innerHeight}
				rowComponent={RowComponent}
				rowCount={itemData.length}
				rowHeight={itemSize}
				overscanCount={5}
			/>
		</Box>
	);
});
